import { Logger, TLogger } from 'components/logger/Logger';
import { StateManager } from 'components/stateManager/StateManager';
import {
  IVideoControllerComponentsCommonProps,
  IVideoElementControllerProps,
} from 'components/videoElementController/IVideoElementController';
import {
  EVideoElementControllerBusEvent,
  TVideoControllerEventBus,
} from 'components/videoElementController/VideoElementController.events';
import throttle from 'lodash-es/throttle';
import { IPlayerState } from 'types/common';
import { bind } from 'utils/bind';
import { BROWSER_CAPABILITIES } from 'utils/browserCapabilities';

const TIMEOUT_UPDATE_STATUS_PLAYING = 2000;
const STORAGE_KEY_WAS_RESTARTED = 'ristrettoWasRestarted';
let timerCheckPrevTime = 0;
let prevTime = 0;

export class TimeUpdateController {
  private _shouldSetNextUpdateVideoDurationId = true;
  private _props: IVideoControllerComponentsCommonProps;
  private _updateVideoDurationId?: ReturnType<typeof setTimeout>;
  private _config: IVideoElementControllerProps = {};
  private _logger: TLogger;

  private _onNativeTimeUpdateHandlerThrottled = throttle(
    this._onNativeTimeUpdateHandler,
    TIMEOUT_UPDATE_STATUS_PLAYING,
    {
      trailing: false,
    },
  );

  private _eventsMap = {
    [EVideoElementControllerBusEvent.TIME_UPDATE_NATIVE]: this._onNativeTimeUpdateHandlerThrottled,
    [EVideoElementControllerBusEvent.DESTROY]: this._destroy,
  };

  private readonly _state: StateManager<IPlayerState>;
  private readonly _eventBus: TVideoControllerEventBus;

  constructor(props: IVideoControllerComponentsCommonProps) {
    this._state = props.state;
    this._eventBus = props.eventBus;
    this._logger = Logger(this._eventBus, 'VideoElementController - TimeUpdateController');
    this._props = props;

    this._logger.info('Created');
    this._logger.debug('Initial state', props.state);
    this._logger.debug('Initial props', props);
  }

  public setConfig(config: IVideoElementControllerProps) {
    this._logger.debug('Config update received, the new one:', config);
    const hasTimeoutStatusPlayingChanged =
      config.timeoutUpdateStatusPlaying !== this._config.timeoutUpdateStatusPlaying;
    this._config = config;
    if (hasTimeoutStatusPlayingChanged) {
      this._eventsMap[EVideoElementControllerBusEvent.TIME_UPDATE_NATIVE] = throttle(
        this._onNativeTimeUpdateHandler,
        config.timeoutUpdateStatusPlaying,
        {
          trailing: false,
        },
      );
      this._eventBus.toggleSubscriptionBulk({
        action: 'unsubscribe',
        callbacks: this._eventsMap,
      });
      this.init();
    }
  }

  @bind
  public init() {
    this._logger.info('Initialization requested, attaching eventBus listeners');
    this._eventBus.toggleSubscriptionBulk({
      action: 'subscribe',
      callbacks: this._eventsMap,
    });
  }

  @bind
  public startTimeUpdate() {
    this._stopTimeUpdate();
    if (!this._shouldSetNextUpdateVideoDurationId || !this._config) {
      this._shouldSetNextUpdateVideoDurationId = true;
      return;
    }

    this._updateVideoDurationId = setTimeout(
      this._onTimeUpdate,
      this._config.timeoutUpdateVideoDuration as number,
    );

    this._publishUpdatePlayerResolutionIfNeeded();
  }

  @bind
  private _onNativeTimeUpdateHandler(e?: { originalEvent: Event }) {
    this._logger.debug('Native video timeUpdate caught');
    if (e?.originalEvent?.defaultPrevented) {
      return;
    }

    const videoElement = this._props.getVideoElement();
    const { canPlay, isPlaying, isLoading, isRestarted } = this._state.getState();

    this._publishUpdatePlayerResolutionIfNeeded();

    if (!canPlay) {
      this._logger.verbose('CanPlay shared state is false. Skipping');
      return;
    }

    this._eventBus.publish(EVideoElementControllerBusEvent.RESET_CHECKING_AUTOPLAY_COUNT, {});

    const isTimeChanged = timerCheckPrevTime !== videoElement?.currentTime;

    this._logger.verbose(`Video current time is ${isTimeChanged ? '' : 'not'} changed`);

    if (isTimeChanged) {
      this._eventBus.publish(EVideoElementControllerBusEvent.CANCEL_RESET_STREAM, {});

      if (
        BROWSER_CAPABILITIES.safari15OrHigher &&
        !isRestarted &&
        !localStorage?.getItem(STORAGE_KEY_WAS_RESTARTED)
      ) {
        localStorage.setItem(STORAGE_KEY_WAS_RESTARTED, '1');
        // This hack must fix very rare bug on safari, when there is a black screen and the video is not playing.
        // This hack also causes unexpected behavior - playback state switches from playing to paused and then back to playing,
        // Which can lead to ui lagging and twitching. We don't want to remove the hack completely, but we want to minimize its usage.
        // So if we had to use the hack, we save a flag in localStorage and won't use the hack on the current browser anymore.
        // The bug is very rare and may reproduce only once - when we first open video on a freshly installed Safari.
        this._eventBus.publish(EVideoElementControllerBusEvent.RESTART, {});
      }

      if (videoElement) {
        timerCheckPrevTime = videoElement.currentTime;
      }

      if (!isPlaying || isLoading) {
        this._eventBus.publish(EVideoElementControllerBusEvent.PLAYING, {});
      }
    } else if (!isLoading) {
      this._eventBus.publish(EVideoElementControllerBusEvent.LOADING, {});
    } else {
      this._eventBus.publish(EVideoElementControllerBusEvent.VIDEO_RESET, {});
    }
  }

  @bind
  private _stopTimeUpdate() {
    clearTimeout(this._updateVideoDurationId);
  }

  @bind
  private _onTimeUpdate() {
    if (!this._state.getState().canPlay) {
      this.startTimeUpdate();

      return;
    }

    const videoElement = this._props.getVideoElement();

    if (videoElement) {
      if (prevTime !== videoElement.currentTime) {
        prevTime = videoElement.currentTime;
      }
    }

    this.startTimeUpdate();
  }

  @bind
  private _publishUpdatePlayerResolutionIfNeeded() {
    const videoElement = this._props.getVideoElement();
    if (videoElement) {
      const { videoWidth, videoHeight } = videoElement;
      const { videoElementResolution } = this._state.getState();

      if (
        (!videoElementResolution ||
          videoElementResolution.height !== videoHeight ||
          videoElementResolution.width !== videoWidth) &&
        this._config.minimalPossibleVideoDimension &&
        Math.min(videoHeight, videoWidth) > (this._config.minimalPossibleVideoDimension as number)
      ) {
        this._logger.verbose(
          `Stored resolution is ${videoElementResolution?.width}x${videoElementResolution?.height}, current is ${videoWidth}x${videoHeight}`,
        );
        this._eventBus.publish(EVideoElementControllerBusEvent.UPDATE_PLAYER_RESOLUTION, {
          width: videoWidth,
          height: videoHeight,
        });
      }
    }
  }

  @bind
  private _destroy() {
    this._logger.info('Destroying');
    this._eventBus.toggleSubscriptionBulk({
      action: 'unsubscribe',
      callbacks: this._eventsMap,
    });

    this._shouldSetNextUpdateVideoDurationId = false;

    this._stopTimeUpdate();
  }
}
